import { useEffect, useMemo, useState } from 'react'

import { Box, Grid, Tag } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog, F0Select } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity, Input } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  AtsContractTypesEnum,
  AtsSettingsOfferLetterTemplatesDocument,
  AtsUpdateOfferLetterTemplateDocument,
  AtsUpdateOfferLetterTemplateGetItemDocument,
} from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UpdateOfferLetterTemplateContentSkeleton } from './Skeleton'

interface FormValues {
  id: string
  name: string
  legalEntityId?: string
  contractType?: AtsContractTypesEnum
  tags?: string[]
}

interface Props {
  onClose: () => void
  id: string
}

export const UpdateOfferLetterTemplateModal = ({ onClose, id }: Props) => {
  const [tagValue, setTagValue] = useState('')
  const [tags, setTags] = useState<string[]>([])

  const { data, loading } = useQuery(AtsUpdateOfferLetterTemplateGetItemDocument, {
    variables: {
      id,
    },
  })

  const legalEntities = data?.companies.legalEntities ?? []
  const contractTypes = data?.ats.contractTypesConnection.nodes || []

  const [updateOfferLetterTemplate, { loading: updating }] = useMutation(
    AtsUpdateOfferLetterTemplateDocument,
    {
      extractorFn: (response) => response.atsMutations.updateAtsOfferLetterTemplate,
      refetchQueries: [AtsSettingsOfferLetterTemplatesDocument],
    }
  )

  const form = useForm<FormValues>({
    defaultValues: {
      id,
      name: data?.ats.offerLetterTemplate?.name ?? '',
      legalEntityId: data?.ats.offerLetterTemplate?.legalEntity?.id ?? undefined,
      contractType: data?.ats.offerLetterTemplate?.contractTypes?.[0] ?? undefined,
      tags: data?.ats.offerLetterTemplate?.tags ?? [],
    },
    validate(values) {
      const errors: FormErrors<FormValues> = {}
      if (!values.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    validateOnChange: false,
    validateOnBlur: false,
    async onSubmit(values, { setErrors }) {
      const result = await updateOfferLetterTemplate({
        variables: {
          id,
          contractTypes: values.contractType ? [values.contractType] : undefined,
          name: values.name,
          legalEntityId: values.legalEntityId,
          tags: tags.length > 0 ? tags : undefined,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success && !!handled.data?.offerLetterTemplate?.id) {
        app.setFlash({
          message: i18n.t('common.saved_successfully'),
          type: 'correct',
        })

        onClose()
      }

      return handled
    },
  })

  useEffect(() => {
    const templateTags = data?.ats.offerLetterTemplate?.tags
    if (templateTags) {
      setTags(templateTags)
    }
  }, [data?.ats.offerLetterTemplate?.tags])

  const formattedContractTypes = contractTypes.map(({ id: contractTypeId, label, country }) => ({
    label,
    country,
    value: contractTypeId,
  }))

  const contractTypesEntities: EntitySelectEntity[] = useMemo(() => {
    const groupedFormattedContractTypes = groupBy(
      formattedContractTypes,
      (item) => item.country ?? 'all'
    )
    const entities: EntitySelectEntity[] = Object.entries(groupedFormattedContractTypes).map(
      ([countryKey, items]) => ({
        id: `group_${countryKey}`,
        name: countryKey,
        expanded: true,
        subItems: items
          .filter((i) => i.label)
          .sort((x, y) => x.label.localeCompare(y.label))
          .map(({ value, label, country: countryCode }) => ({
            subId: value,
            subName: label,
            subSearchKeys: [label, value, countryCode ?? ''],
          })),
      })
    )
    return entities
  }, [formattedContractTypes])

  const selectedEntities: EntitySelectEntity[] = useMemo(() => {
    const selectedId = form.values.contractType
    if (!selectedId) return []

    for (const group of contractTypesEntities) {
      const sub = group.subItems?.find((s) => s.subId === selectedId)
      if (sub) return [{ ...group, subItems: [sub] }]
    }

    return []
  }, [contractTypesEntities, form.values.contractType])

  const addTag = () => {
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue])
    } else {
      app.setFlash({
        message: i18n.t(
          'ats.settings.templates.offer_letter_templates.create_modal.tag_already_added'
        ),
        type: 'wrong',
      })
    }
    setTagValue('')
  }
  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag))
  }

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      width="lg"
      title={i18n.t('common.edit')}
      primaryAction={{
        label: i18n.t('common.save'),
        onClick: () => form.handleSubmit(),
        loading: updating,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <UpdateOfferLetterTemplateContentSkeleton />
      ) : (
        <Box gap="s16">
          <Box width="full" gap="s16">
            <Input
              label={i18n.t(
                'ats.settings.templates.offer_letter_templates.create_modal.name_input_label'
              )}
              name="name"
              placeholder={i18n.t(
                'ats.settings.templates.offer_letter_templates.create_modal.name_input_placeholder'
              )}
              size="md"
              onChange={(value) => form.setFieldValue('name', value)}
              value={form.values.name}
              error={form.errors.name}
            />
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0Select
                  label={i18n.t(
                    'ats.settings.templates.offer_letter_templates.create_modal.legal_entity_input_label'
                  )}
                  placeholder={i18n.t('common.select')}
                  name="legalEntityId"
                  value={form.values.legalEntityId}
                  onChange={(value: string) => form.setFieldValue('legalEntityId', value)}
                  options={legalEntities.map((entity) => ({
                    label: `${entity.legalName} - ${entity.currency}`,
                    value: entity.id,
                  }))}
                  size="md"
                  showSearchBox
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <EntitySelect
                  label={i18n.t(
                    'ats.settings.templates.offer_letter_templates.create_modal.contract_type_input_label'
                  )}
                  notFoundTitle={i18n.t(
                    'ats.settings.templates.offer_letter_templates.create_modal.contract_type_input_not_found'
                  )}
                  notFoundSubtitle={''}
                  placeholder={i18n.t('common.select')}
                  onSelect={(entity) => {
                    if (!entity?.subItems?.[0]) {
                      return
                    }
                    const contractTypeId = entity.subItems[0].subId
                    if (contractTypeId) {
                      form.setFieldValue('contractType', contractTypeId)
                    }
                  }}
                  entities={contractTypesEntities}
                  groups={[]}
                  selectedGroup="all"
                  onItemExpandedChange={() => false}
                  onGroupChange={() => false}
                  selectedEntities={selectedEntities}
                  selectedItemsCopy={i18n.t('common.selected')}
                  hiddenAvatar
                  singleSelector
                  size="md"
                  loading={loading}
                />
              </Box>
            </Grid>
            <Box
              gap="s16"
              justifyContent="spaceBetween"
              flexDirection="row"
              flexWrap="noWrap"
              alignItems="flexEnd"
              width="full"
            >
              <Box flexGrow>
                <Input
                  label={i18n.t(
                    'ats.settings.templates.offer_letter_templates.create_modal.tags_input_label'
                  )}
                  name="tags"
                  placeholder={i18n.t(
                    'ats.settings.templates.offer_letter_templates.create_modal.tags_input_placeholder'
                  )}
                  size="md"
                  onPressEnter={() => tagValue && addTag()}
                  onChange={(value) => setTagValue(value)}
                  value={tagValue}
                />
              </Box>
              <F0Button
                onClick={() => addTag()}
                label={i18n.t('common.add')}
                variant="outline"
                icon={Add}
                disabled={!tagValue}
                size="lg"
              />
            </Box>
            <Box flexDirection="row" gap="s12" alignItems="center" flexWrap="wrap">
              {tags.map((tag, index) => (
                <Tag key={index} rightItem="close" type="viridian" onClose={() => removeTag(tag)}>
                  {tag}
                </Tag>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </F0Dialog>
  )
}
